// What the Upload progress toast is currently watching (owner, 2026-09-03).
//
// WHY THIS IS NOT COMPONENT STATE. The progress used to live inside the Upload
// dialog, which meant it died the moment the dialog closed — and closing the
// dialog is the first thing an uploader does after pressing Send. The owner
// asked for a toast on the right of the screen that "stays while those files
// are being processed", so the list of what is being watched has to outlive the
// screen that started it.
//
// A MODULE-LEVEL STORE rather than a context, deliberately. The producer
// (`UploadScreen`, mounted inside a dialog) and the consumer
// (`UploadProgressToast`, mounted in the app shell) are on unrelated branches
// of the tree, so a provider would have to wrap the whole app to join two
// components. `useSyncExternalStore` gives the same subscription in a file the
// tests can drive directly, without a renderer — which is what the app's
// `environment: 'node'` suite needs.
//
// NO PHI, and nothing here logs. A tracked entry is an upload id and the file
// name the uploader chose, which is the same pair `messages.ts` already rules
// is safe to carry.
//
// THE SEND LIVES HERE TOO (owner, 2026-09-29, D-205): "the pop-up should close
// automatically after this button is clicked." The dialog unmounts the moment
// it closes, so a send loop held in the dialog would lose its failures with
// it. `sendUploads` runs the loop at module level instead, and puts each file
// in the toast BEFORE its bytes go up: it shows as Uploading…, becomes the
// server's own stages once registered, or stays as Not sent with the API's
// reason. That last row is the only place a failed send is reported now.
import { useSyncExternalStore } from 'react'
import { errorMessage } from '../api/errors'
import { uploadDocument } from './data'

export interface TrackedUpload {
  /** The upload id `POST /uploads/register` answered with, and the poll key.
      Until then, a local key (`local:N`) that is never polled. */
  id: string
  /** What to call it on screen. The uploader's own file name. */
  fileName: string
  /** When this was tracked, for stable ordering across re-renders. */
  startedAt: number
  /** The send's own state, before the server has a row to report on. Absent
      once the upload registered: from then on the server's stage is the
      answer. */
  send?: 'uploading' | 'send_failed'
  /** The API's own wording for why the send failed. */
  error?: string
}

let tracked: readonly TrackedUpload[] = []
let localSeq = 0
const listeners = new Set<() => void>()

function emit(): void {
  for (const listener of listeners) listener()
}

/** Whether this entry has a server row to poll. */
export function isRegistered(entry: TrackedUpload): boolean {
  return entry.send === undefined
}

function replace(id: string, next: TrackedUpload | null): void {
  tracked = tracked.flatMap((entry) => (entry.id === id ? (next ? [next] : []) : [entry]))
  emit()
}

type Uploader = (file: File, companyId: string | null) => Promise<{ id: string }>

/**
 * Send files one after another, reporting each in the toast from the moment
 * it starts. Fire and forget: the caller does not wait, because the dialog
 * that calls it has already closed. `upload` is a test seam.
 *
 * THE SAME company for every file (owner item 21): there is one picker, and
 * its one value is passed in once.
 */
export async function sendUploads(
  files: readonly File[],
  companyId: string | null,
  upload: Uploader = uploadDocument,
): Promise<{ sent: number; failed: number }> {
  // Every row appears at once, so the toast shows the whole batch straight
  // away rather than one file at a time as each one's bytes finish.
  const keys = files.map((file) => {
    const key = `local:${++localSeq}`
    tracked = [...tracked, { id: key, fileName: file.name, startedAt: Date.now(), send: 'uploading' }]
    return key
  })
  if (keys.length > 0) emit()
  let sent = 0
  let failed = 0
  for (let i = 0; i < files.length; i++) {
    const key = keys[i]
    const local = tracked.find((entry) => entry.id === key)
    // Dismissed from the toast mid-send. The send still goes ahead — the
    // dismiss closes the panel, it is not a cancel — but it is not re-listed.
    try {
      const registered = await upload(files[i], companyId)
      sent++
      if (!local || !tracked.some((entry) => entry.id === key)) continue
      // A retried register can answer an id already listed; keep one row.
      const known = tracked.some((entry) => entry.id === registered.id)
      replace(key, known ? null : { id: registered.id, fileName: local.fileName, startedAt: local.startedAt })
    } catch (e) {
      failed++
      if (!local || !tracked.some((entry) => entry.id === key)) continue
      replace(key, { ...local, send: 'send_failed', error: errorMessage(e) })
    }
  }
  return { sent, failed }
}

/** Stop watching everything — the toast dismissing, or the user closing it. */
export function clearTrackedUploads(): void {
  if (tracked.length === 0) return
  tracked = []
  emit()
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener)
  return () => {
    listeners.delete(listener)
  }
}

function snapshot(): readonly TrackedUpload[] {
  return tracked
}

/** The tracked list, re-rendering the caller whenever it changes. */
export function useTrackedUploads(): readonly TrackedUpload[] {
  return useSyncExternalStore(subscribe, snapshot, snapshot)
}

/** Test seam: read the list without a renderer. */
export function trackedUploads(): readonly TrackedUpload[] {
  return tracked
}
